import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Box, FormControl, InputLabel, Select, MenuItem, CircularProgress, SelectChangeEvent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { accountingService } from '../services/accountingService';
import { NewPaymentReceived } from '../types/accounting';
import { Project } from '../types/project';
import { projectService } from '../services/projectService';
import { User } from '../types/user';
import { userService } from '../services/userService';

interface AddPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({ open, onClose, onSave }) => {
  const [newPayment, setNewPayment] = useState<Partial<NewPaymentReceived>>({});
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const fetchInitialData = async () => {
        setLoading(true);
        try {
          const projectData = await projectService.getProjects();
          setProjects(projectData);
          const userData = await userService.getUsers();
          setUsers(userData);
        } catch (err) {
          setError('Could not load projects or users.');
        } finally {
          setLoading(false);
        }
      };
      fetchInitialData();
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>) => {
    const { name, value } = e.target;
    setNewPayment(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setNewPayment(prev => ({ ...prev, date_received: format(date, 'yyyy-MM-dd') }));
    }
  };

  const handleSubmit = async () => {
    if (!newPayment.project || !newPayment.amount || !newPayment.date_received || !newPayment.description || !newPayment.received_by) {
      setError('Project, amount, date, description, and received by are required.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await accountingService.createPayment(newPayment as NewPaymentReceived);
      onSave();
      onClose();
      setNewPayment({}); // Reset form
    } catch (err: any) {
      console.error('Failed to create payment:', err);
      setError(err.response?.data?.detail || 'An unexpected error occurred.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Payment Received</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? <CircularProgress /> : (
            <>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Project</InputLabel>
                <Select name="project" value={newPayment.project || ''} label="Project" onChange={handleChange}>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField margin="normal" required fullWidth label="Amount" name="amount" type="number" onChange={handleChange} />
              <DatePicker label="Date of Payment" value={newPayment.date_received ? new Date(newPayment.date_received) : null} onChange={handleDateChange} sx={{ width: '100%', mt: 2, mb: 1 }} />
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Received By</InputLabel>
                <Select name="received_by" value={newPayment.received_by || ''} label="Received By" onChange={handleChange}>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>{user.first_name} {user.last_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField margin="normal" required fullWidth label="Description" name="description" onChange={handleChange} multiline rows={3} helperText="e.g., Bank Transfer, Check, Cash" />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving || loading}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPaymentModal;